import React, { useState, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Calendar as CalendarIcon,
  List,
  LayoutGrid,
  Plus,
  ChevronLeft,
  ChevronRight,
  Filter,
  Eye,
  Globe,
  Building2,
  Film as FilmIcon,
} from 'lucide-react';
import {
  format,
  startOfMonth,
  endOfMonth,
  addMonths,
  subMonths,
  isSameMonth,
  isToday,
} from 'date-fns';

import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Popover, PopoverTrigger, PopoverContent } from '../components/ui/popover';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Tooltip, TooltipTrigger, TooltipContent } from '../components/ui/tooltip';
import { useEvents } from '../hooks/useEvents';
import { useWorkspace } from '../context/WorkspaceContext';
import MonthView from '../components/calendar/MonthView';
import AgendaView from '../components/calendar/AgendaView';
import EventFormDialog from '../components/calendar/EventFormDialog';
import EventDetailSheet from '../components/calendar/EventDetailSheet';

import { EVENT_TYPE_CONFIG, ALL_EVENT_TYPES } from '../components/calendar/eventConstants';

const SCOPE_META = {
  account:   { icon: Globe,     label: 'All Workspaces', badge: 'All Workspaces', readonly: true,  modeLabel: 'Browsing' },
  workspace: { icon: Building2, label: 'Workspace',      badge: 'Workspace',      readonly: true,  modeLabel: 'Browsing' },
  film:      { icon: FilmIcon,  label: 'Film',           badge: 'Film',           readonly: false, modeLabel: null },
};

export default function CalendarPage() {
  const { workspaceId, filmId } = useParams();
  const navigate = useNavigate();
  const { currentWorkspace, workspaces } = useWorkspace();

  /* ── Scope derivation ──────────────────────────────────────────── */
  const scope = filmId ? 'film' : workspaceId ? 'workspace' : 'account';
  const scopeMeta = SCOPE_META[scope];
  const isReadOnly = scopeMeta.readonly;

  /* ── Local UI state ────────────────────────────────────────────── */
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [view, setView] = useState('month'); // month | agenda
  const [activeTypes, setActiveTypes] = useState(ALL_EVENT_TYPES);
  const [formOpen, setFormOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [detailEvent, setDetailEvent] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);

  /* ── Date range for the query (padded to full month) ───────────── */
  const queryStart = useMemo(() => startOfMonth(currentMonth).toISOString(), [currentMonth]);
  const queryEnd = useMemo(() => endOfMonth(currentMonth).toISOString(), [currentMonth]);

  /* ── Fetch events ──────────────────────────────────────────────── */
  const { data: events = [], isLoading, error } = useEvents({
    scope,
    workspace_id: workspaceId,
    film_id: filmId,
    start_at: queryStart,
    end_at: queryEnd,
    event_type: activeTypes.length < ALL_EVENT_TYPES.length ? activeTypes : undefined,
  });

  /* ── Handlers ──────────────────────────────────────────────────── */
  const prevMonth = useCallback(() => setCurrentMonth((m) => subMonths(m, 1)), []);
  const nextMonth = useCallback(() => setCurrentMonth((m) => addMonths(m, 1)), []);
  const goToday = useCallback(() => setCurrentMonth(new Date()), []);

  const toggleType = useCallback((type) => {
    setActiveTypes((prev) => {
      if (prev.includes(type)) {
        const next = prev.filter((t) => t !== type);
        return next.length === 0 ? ALL_EVENT_TYPES : next; // never empty
      }
      return [...prev, type];
    });
  }, []);

  const handleEventClick = useCallback((event) => {
    setDetailEvent(event);
  }, []);

  const handleEditEvent = useCallback((event) => {
    setDetailEvent(null);
    setEditingEvent(event);
    setFormOpen(true);
  }, []);

  const handleCreateEvent = useCallback((date) => {
    setSelectedDate(date || null);
    setEditingEvent(null);
    setFormOpen(true);
  }, []);

  const handleFormClose = useCallback(() => {
    setFormOpen(false);
    setEditingEvent(null);
    setSelectedDate(null);
  }, []);

  /* ── Title derivation ──────────────────────────────────────────── */
  const wsName = currentWorkspace?.name || workspaces?.find(w => w.id === workspaceId)?.name || '';

  return (
    <div className="flex flex-col gap-6 p-4 md:p-6 lg:p-8 max-w-[1400px] mx-auto w-full">
      {/* ── Hero band ──────────────────────────────────────────── */}
      <div className="flex flex-col gap-1">
        <p className="text-xs font-semibold uppercase tracking-widest text-primary">
          Production Calendar
        </p>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-3">
            <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight">
              Calendar
            </h1>
            {(() => {
              const badge = (
                <Badge
                  variant="outline"
                  data-testid="page-scope-badge"
                  data-scope={scope}
                  data-readonly={isReadOnly ? 'true' : 'false'}
                  className={`gap-1 text-xs ${isReadOnly ? 'border-muted-foreground/30' : 'border-primary/40 text-primary'}`}
                >
                  {isReadOnly ? <Eye className="h-3 w-3" /> : <scopeMeta.icon className="h-3 w-3" />}
                  {scopeMeta.modeLabel ? `${scopeMeta.modeLabel} · ${scopeMeta.badge}` : scopeMeta.badge}
                </Badge>
              );
              return isReadOnly ? (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span tabIndex={0} className="inline-flex outline-none">{badge}</span>
                  </TooltipTrigger>
                  <TooltipContent>Open a film to add items.</TooltipContent>
                </Tooltip>
              ) : badge;
            })()}
          </div>

          {/* Create button — film scope only */}
          {!isReadOnly && (
            <Button onClick={() => handleCreateEvent()} className="gap-2 shrink-0">
              <Plus className="h-4 w-4" />
              New Event
            </Button>
          )}
        </div>
        {scope === 'workspace' && wsName && (
          <p className="text-sm text-muted-foreground">Events across all films in {wsName}</p>
        )}
      </div>

      {/* ── Toolbar ────────────────────────────────────────────── */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 bg-card rounded-xl border p-3">
        {/* Month navigation */}
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={prevMonth} className="h-8 w-8">
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <button
            onClick={goToday}
            className="font-display text-lg font-semibold min-w-[180px] text-center hover:text-primary transition-colors"
          >
            {format(currentMonth, 'MMMM yyyy')}
          </button>
          <Button variant="outline" size="icon" onClick={nextMonth} className="h-8 w-8">
            <ChevronRight className="h-4 w-4" />
          </Button>
          {!isSameMonth(currentMonth, new Date()) && (
            <Button variant="ghost" size="sm" onClick={goToday} className="text-xs">
              Today
            </Button>
          )}
        </div>

        <div className="flex items-center gap-2">
          {/* Type filter */}
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" className="gap-1.5 h-8 text-xs">
                <Filter className="h-3.5 w-3.5" />
                Filter
                {activeTypes.length < ALL_EVENT_TYPES.length && (
                  <span className="bg-primary text-primary-foreground rounded-full px-1.5 text-[10px] font-bold">
                    {activeTypes.length}
                  </span>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-56 p-2" align="end">
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground px-2 py-1">
                Event Types
              </p>
              {ALL_EVENT_TYPES.map((type) => {
                const cfg = EVENT_TYPE_CONFIG[type];
                const active = activeTypes.includes(type);
                return (
                  <button
                    key={type}
                    onClick={() => toggleType(type)}
                    className={`flex items-center gap-2 w-full rounded-md px-2 py-1.5 text-sm transition-colors ${
                      active ? 'bg-accent' : 'opacity-50 hover:opacity-75'
                    }`}
                  >
                    <span className={`h-2.5 w-2.5 rounded-full ${cfg.dot}`} />
                    {cfg.label}
                  </button>
                );
              })}
            </PopoverContent>
          </Popover>

          {/* View toggle */}
          <Tabs value={view} onValueChange={setView}>
            <TabsList className="h-8">
              <TabsTrigger value="month" className="gap-1 text-xs px-2.5 h-7">
                <LayoutGrid className="h-3.5 w-3.5" />
                Month
              </TabsTrigger>
              <TabsTrigger value="agenda" className="gap-1 text-xs px-2.5 h-7">
                <List className="h-3.5 w-3.5" />
                Agenda
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
      </div>

      {/* ── Content ────────────────────────────────────────────── */}
      {isLoading ? (
        <CalendarSkeleton view={view} />
      ) : error ? (
        <div className="rounded-xl border border-destructive/30 bg-destructive/5 p-8 text-center">
          <p className="text-destructive font-medium">Failed to load events</p>
          <p className="text-sm text-muted-foreground mt-1">{error.response?.data?.detail || error.message}</p>
        </div>
      ) : view === 'month' ? (
        <MonthView
          events={events}
          currentMonth={currentMonth}
          onEventClick={handleEventClick}
          onDayClick={!isReadOnly ? handleCreateEvent : undefined}
          isReadOnly={isReadOnly}
        />
      ) : (
        <AgendaView
          events={events}
          currentMonth={currentMonth}
          onEventClick={handleEventClick}
          isReadOnly={isReadOnly}
        />
      )}

      {/* ── Event detail sheet ─────────────────────────────────── */}
      <EventDetailSheet
        event={detailEvent}
        open={!!detailEvent}
        onClose={() => setDetailEvent(null)}
        onEdit={!isReadOnly ? handleEditEvent : undefined}
        isReadOnly={isReadOnly}
      />

      {/* ── Create / Edit dialog (film scope only) ─────────────── */}
      {!isReadOnly && (
        <EventFormDialog
          open={formOpen}
          onClose={handleFormClose}
          filmId={filmId}
          event={editingEvent}
          defaultDate={selectedDate}
        />
      )}
    </div>
  );
}

/* ── Loading skeleton ──────────────────────────────────────────── */
function CalendarSkeleton({ view }) {
  if (view === 'agenda') {
    return (
      <div className="space-y-4">
        {[...Array(5)].map((_, i) => (
          <div key={`sk-row-${i}`} className="flex gap-4">
            <Skeleton className="h-12 w-12 rounded-lg" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-4 w-48" />
              <Skeleton className="h-3 w-32" />
            </div>
          </div>
        ))}
      </div>
    );
  }
  return (
    <div className="rounded-xl border bg-card p-4">
      <div className="grid grid-cols-7 gap-1">
        {[...Array(7)].map((_, i) => (
          <Skeleton key={`h-${i}`} className="h-6 rounded" />
        ))}
        {[...Array(35)].map((_, i) => (
          <Skeleton key={`sk-cell-${i}`} className="h-24 rounded-lg" />
        ))}
      </div>
    </div>
  );
}
